import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';

import { createContractTemplate, getContractLogoSeal, updateContractTemplate } from '../../../api/contract';
import ElectronicContractManage from './ElectronicContractManage';
import ElectronicContractList from './ElectronicContractList';
import ElectronicContractCreate from './ElectronicContractCreate';
import ElectronicContractPreview from './ElectronicContractPreview';
import ElectronicContractDetail from './ElectronicContractDetail';
import { PageRoot, SubTabRow, SubTabButton } from './ElectronicContractStyled';

// 조항 유형 enum → 한글 라벨
export const CLAUSE_TYPE_LABELS = {
  TERMS_OF_USE: '이용약관',
  REFUND_POLICY: '환불 정책',
  PRIVACY_POLICY: '개인정보 처리방침',
  TRANSFER_POLICY: '양도 정책',
  ADVERTISING_POLICY: '광고성 정보 수신 동의',
};

// 만들기 폼의 약관 입력 항목 정의
// - clauseType: API enum (저장/조회 키)
// - label/placeholder: 입력 카드 표기
// - branchField: 지점 상세(약관)에서 내용을 끌어오는 필드
export const CLAUSE_FIELDS = [
  {
    clauseType: 'TERMS_OF_USE',
    label: '센터 이용 약관',
    placeholder: '센터 이용 약관을 입력해 주세요.',
    branchField: 'termsOfUse',
  },
  {
    clauseType: 'REFUND_POLICY',
    label: '환불 약관',
    placeholder: '환불 약관을 입력해 주세요.',
    branchField: 'refundPolicy',
  },
  {
    clauseType: 'PRIVACY_POLICY',
    label: '개인정보 이용 동의',
    placeholder: '개인정보 이용 동의를 입력해 주세요.',
    branchField: 'privacyPolicy',
  },
  {
    clauseType: 'ADVERTISING_POLICY',
    label: '광고성 정보 이용 동의',
    placeholder: '광고성 정보 이용 동의를 입력해 주세요.',
    branchField: 'advertisingPolicy',
  },
  {
    clauseType: 'TRANSFER_POLICY',
    label: '양도/양수 약관',
    placeholder: '양도/양수 약관을 입력해 주세요.',
    branchField: 'transferPolicy',
  },
];

// clauses 는 선택된 clauseType 의 배열(순서 = sortOrder)로 관리한다.
// sealId/logoId 는 계약서에 연결할 직인·로고(contract_logo_seal FK). 미리보기 노출 + 저장에 함께 사용한다.
// showSeal/showLogo 토글이 켜져 있고 항목이 선택된 경우에만 연결한다.
const EMPTY_FORM = { name: '', category: '', clauses: [], showSeal: false, showLogo: false, sealId: null, logoId: null, termDrafts: {} };

const ElectronicContract = ({ branchId, branchDetailInfo }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const token = useSelector(state => state.user.user?.token);

  // 마운트 시 history.state 에서 이전 view 복원 (회원상세 → 뒤로가기 시 계약서 상세로 복귀)
  const savedState = window.history.state;
  const initialView = savedState?.contractView || 'list';
  const initialDetailId = savedState?.contractDetailId || null;

  // 화면 모드 — 'list'(목록/관리 탭) | 'create'(계약서 정보 입력) | 'preview'(미리보기) | 'detail'(계약 상세)
  const [view, setView] = useState(initialView);
  // 보조 탭 — 계약서 관리(템플릿 표) / 계약서 목록(체결 내역 표)
  const [subTab, setSubTab] = useState('manage');
  // 수정 대상 id (null 이면 신규 생성)
  const [editingId, setEditingId] = useState(null);
  // 상세보기 대상 계약 id
  const [detailId, setDetailId] = useState(initialDetailId);
  // 만들기/미리보기에서 공유하는 폼 상태
  const [form, setForm] = useState(EMPTY_FORM);

  // ── 브라우저 뒤로가기 지원 ──
  // view 전환 시 pushState로 히스토리 항목을 쌓고,
  // 브라우저 뒤로가기(popstate) 시 이전 view로 복귀한다.
  const historyDepthRef = useRef(initialView !== 'list' ? 1 : 0);
  const isPopstateRef = useRef(false);

  const navigateView = useCallback((nextView, extra = {}) => {
    window.history.pushState({ contractView: nextView, ...extra }, '');
    historyDepthRef.current += 1;
    setView(nextView);
  }, []);

  useEffect(() => {
    const handlePopState = (e) => {
      isPopstateRef.current = true;
      historyDepthRef.current = Math.max(0, historyDepthRef.current - 1);
      const prevView = e.state?.contractView || 'list';
      setView(prevView);
      if (prevView === 'detail') {
        setDetailId(e.state?.contractDetailId || null);
      }
      if (prevView === 'list') {
        setForm(EMPTY_FORM);
        setEditingId(null);
        setDetailId(null);
      }
      setTimeout(() => { isPopstateRef.current = false; }, 0);
    };

    window.addEventListener('popstate', handlePopState);
    // 초기 view가 list일 때만 replaceState (복원된 경우는 건드리지 않음)
    if (initialView === 'list') {
      window.history.replaceState({ contractView: 'list' }, '');
    }

    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // 로고/직인 목록 ({ logos: [], seals: [] }) — 미리보기 표시 + 로고직인 모달에서 사용
  // 아직 등록 전이면 404가 날 수 있으므로 그 경우는 빈 목록으로 조용히 처리한다.
  const { data: logoSeal = { logos: [], seals: [] } } = useQuery({
    queryKey: ['contractLogoSeal', branchId],
    queryFn: async () => {
      try {
        return await getContractLogoSeal(branchId, token);
      } catch (error) {
        if (error?.response?.status === 404) return { logos: [], seals: [] };
        throw error;
      }
    },
    enabled: !!branchId && !!token,
  });

  // 조항 내용은 지점 상세(약관)에서 가져온다. 등록 전이면 빈 값.
  const clauseContentMap = useMemo(
    () => ({
      TERMS_OF_USE: branchDetailInfo?.termsOfUse || '',
      REFUND_POLICY: branchDetailInfo?.refundPolicy || '',
      PRIVACY_POLICY: branchDetailInfo?.privacyPolicy || '',
      TRANSFER_POLICY: branchDetailInfo?.transferPolicy || '',
      ADVERTISING_POLICY: branchDetailInfo?.advertisingPolicy || '',
    }),
    [branchDetailInfo],
  );

  const saveMutation = useMutation({
    mutationFn: () => {
      // 토글이 켜져 있고 항목이 선택된 경우에만 로고/직인을 연결한다. 그 외에는 미연결.
      const selectedLogoId = form.showLogo ? form.logoId : null;
      const selectedSealId = form.showSeal ? form.sealId : null;
      const payload = {
        name: form.name,
        category: form.category,
        // 선택된 조항만 sortOrder 와 함께 전송 (내용은 백엔드가 지점 데이터로 채움)
        clauses: form.clauses.map((clauseType, idx) => ({ clauseType, sortOrder: idx + 1 })),
      };
      if (editingId) {
        // 수정: 미연결을 명시적으로 전달해야 하므로 null → 0(해제)으로 보낸다. (명세: 0=해제, null=유지)
        return updateContractTemplate(
          editingId,
          { ...payload, logoId: selectedLogoId ?? 0, sealId: selectedSealId ?? 0 },
          token,
        );
      }
      // 생성: 선택 없으면 null(미연결)
      return createContractTemplate(
        { ...payload, branchId, logoId: selectedLogoId, sealId: selectedSealId },
        token,
      );
    },
    onSuccess: () => {
      toast.success(editingId ? t('성공적으로 수정되었습니다') : t('성공적으로 생성되었습니다'));
      queryClient.invalidateQueries({ queryKey: ['contractTemplates'] });
      if (editingId) queryClient.invalidateQueries({ queryKey: ['contractTemplate', editingId] });
      backToList();
    },
  });

  // "목록으로" 버튼 또는 저장 성공 시 — 쌓인 히스토리를 한 번에 되감는다.
  const backToList = useCallback(() => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setDetailId(null);
    setView('list');
    if (!isPopstateRef.current && historyDepthRef.current > 0) {
      const steps = historyDepthRef.current;
      historyDepthRef.current = 0;
      window.history.go(-steps);
    }
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    navigateView('create');
  };

  // 수정: 상세 로드는 Create 가 editingId 로 직접 조회해 form 을 채운다.
  const openEdit = id => {
    setEditingId(id);
    navigateView('create');
  };

  const handleGenerate = () => {
    if (!form.name.trim()) return toast.error(t('계약서 명을 입력해 주세요.'));
    if (!form.category) return toast.error(t('카테고리를 선택해 주세요.'));
    if (form.clauses.length === 0) return toast.error(t('약관을 한 개 이상 선택해 주세요.'));
    saveMutation.mutate();
  };

  const openDetail = id => {
    setDetailId(id);
    navigateView('detail', { contractDetailId: id });
  };

  // 계약서 만들기 흐름(입력 → 미리보기)은 목록/관리 화면을 대체한다.
  if (view === 'detail') {
    return (
      <ElectronicContractDetail
        contractId={detailId}
        onBack={backToList}
      />
    );
  }
  if (view === 'create') {
    return (
      <ElectronicContractCreate
        editingId={editingId}
        form={form}
        setForm={setForm}
        clauseContentMap={clauseContentMap}
        logoSeal={logoSeal}
        onBack={backToList}
        onNext={() => navigateView('preview')}
      />
    );
  }
  if (view === 'preview') {
    return (
      <ElectronicContractPreview
        form={form}
        isEditing={!!editingId}
        clauseContentMap={{ ...clauseContentMap, ...form.termDrafts }}
        logoSeal={logoSeal}
        branchName={branchDetailInfo?.branchName}
        isSaving={saveMutation.isPending}
        onBack={backToList}
        onPrev={() => window.history.back()}
        onGenerate={handleGenerate}
      />
    );
  }

  return (
    <PageRoot>
      <SubTabRow>
        <SubTabButton active={subTab === 'manage'} onClick={() => setSubTab('manage')}>
          {t('계약서 관리')}
        </SubTabButton>
        <SubTabButton active={subTab === 'list'} onClick={() => setSubTab('list')}>
          {t('계약서 목록')}
        </SubTabButton>
      </SubTabRow>

      {subTab === 'manage' ? (
        <ElectronicContractManage branchId={branchId} logoSeal={logoSeal} onCreate={openCreate} onEdit={openEdit} />
      ) : (
        <ElectronicContractList branchId={branchId} onDetail={openDetail} />
      )}
    </PageRoot>
  );
};

export default ElectronicContract;
